import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type {
  ChannexQuarantinedRevision,
  ChannexRevisionHoldList,
  OperationsDayResponse,
} from '@/shared/api';
import { REVISION_HOLDS_KEY } from './useRevisionHolds';

export type RemovalTarget = Readonly<Pick<ChannexQuarantinedRevision, 'id' | 'propertyGroupId'>>;
export interface RemovalRequest extends RemovalTarget {
  readonly listedAtStart: boolean;
  readonly operations: readonly (readonly [QueryKey, OperationsDayResponse | undefined])[];
}

export function captureRemovalRequest(client: QueryClient, target: RemovalTarget): RemovalRequest {
  return Object.freeze({
    ...target,
    listedAtStart: client
      .getQueriesData<ChannexRevisionHoldList>({ queryKey: REVISION_HOLDS_KEY })
      .some(([, list]) => list?.items.some((row) => row.id === target.id)),
    operations: target.propertyGroupId
      ? client.getQueriesData<OperationsDayResponse>({
          queryKey: ['operations', target.propertyGroupId],
        })
      : [],
  });
}

export async function confirmRemovalInCache(
  client: QueryClient,
  request: RemovalRequest,
  removedAt: string,
) {
  await Promise.all([
    client.cancelQueries({ queryKey: REVISION_HOLDS_KEY }),
    ...(request.propertyGroupId
      ? [client.cancelQueries({ queryKey: ['operations', request.propertyGroupId] })]
      : []),
  ]);
  client.setQueriesData<ChannexRevisionHoldList>({ queryKey: REVISION_HOLDS_KEY }, (list) => {
    if (!list?.items.some((row) => row.id === request.id)) return list;
    return {
      ...list,
      items: list.items.filter((row) => row.id !== request.id),
      quarantinedTotal: Math.max(0, list.quarantinedTotal - 1),
    };
  });
  if (!request.listedAtStart) return;
  for (const [key, before] of request.operations) {
    client.setQueryData<OperationsDayResponse>(key, (day) => {
      // A refreshed snapshot may already include removal; its count must not be decremented twice.
      if (!day || day !== before || Date.parse(day.asOfUtc) > Date.parse(removedAt)) return day;
      return {
        ...day,
        attention: day.attention
          .map((item) =>
            item.code === 'ota_booking_quarantined'
              ? { ...item, count: Math.max(0, item.count - 1) }
              : item,
          )
          .filter((item) => item.count > 0),
      };
    });
  }
}

export async function refreshRemovalQueries(client: QueryClient, target: RemovalTarget) {
  await Promise.all([
    client.invalidateQueries({ queryKey: REVISION_HOLDS_KEY }),
    ...(target.propertyGroupId
      ? [client.invalidateQueries({ queryKey: ['operations', target.propertyGroupId] })]
      : []),
  ]);
}
